import Pristine from 'pristinejs';
import { resetForm, updateInputs, showSuccess, showError } from './forms';
import { setLoading, disableScrolling, enableScrolling, getPageButtons,
  getTabbableElements, wait, setInput } from './utils';
import { generateTransformedImageUrl, SMALL_PLACEHOLDER_IMAGE_URL } from '../../../app/Libraries/Utils';

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const listDrawer = document.getElementById('js-list-drawer');

if (!listDrawer) {
  throw new Error('List drawer element not found');
}

const initialTabbables = getTabbableElements(listDrawer);

initialTabbables.forEach((el) => {
  el.setAttribute('tabindex', '-1');
});

let lastFocusedElement: HTMLElement | null = null;

const closeBtns = listDrawer.querySelectorAll('.js-close-btn');
const drawerPages = listDrawer.querySelectorAll<HTMLElement>('.js-list-drawer-page');
const addToListBtns = document.querySelectorAll<HTMLButtonElement>('.js-add-to-list');
const createListBtns = document.querySelectorAll<HTMLButtonElement>('.js-create-new-list');
const editListBtns = document.querySelectorAll<HTMLButtonElement>('.js-edit-list');
const addForm = listDrawer.querySelector<HTMLFormElement>('.js-add-to-list-form');
const listOverlay = listDrawer.querySelector('.list-drawer__overlay');
const createListForm = listDrawer?.querySelector <HTMLFormElement>('.js-create-list-form');
const editListForm = listDrawer?.querySelector<HTMLFormElement>('.js-edit-list-form');
const backBtn = listDrawer?.querySelector<HTMLButtonElement>('.js-list-drawer-back');

let selectedAddToListQuantity = 1;

type DrawerState = 'loggedout' | 'empty' | 'create' | 'add' | 'edit';

let drawerState: DrawerState = (window as any).IS_LOGGED_IN === 'true' ? 'add' : 'loggedout';

const isLoggedIn = (window as any).IS_LOGGED_IN === 'true';
let shouldRefreshAfterCreate = true;

closeBtns.forEach((btn) => btn.addEventListener('click', closeListDrawer));
listOverlay?.addEventListener('click', closeListDrawer);

export function openListDrawer() {
  if (!listDrawer) {
    return;
  }

  lastFocusedElement = document.activeElement as HTMLElement;

  listDrawer.classList.add('open');
  listDrawer.removeAttribute('aria-hidden');
  disableScrolling();
  const event = new Event('listDrawer:opened');
  document.dispatchEvent(event);

  const pageBtns = getPageButtons();
  pageBtns.forEach((btn) => {
    btn.setAttribute('tabindex', '-1');
    (btn as HTMLElement).dataset.tabindex = 'modified';
  });

  const tabbables = getTabbableElements(listDrawer);
  tabbables.forEach((el) => {
    el.setAttribute('tabindex', '0');
    el.dataset.tabindex = 'modified';
  });

  // Move focus to the cart drawer
  listDrawer.focus({ preventScroll: true });
}

export function closeListDrawer() {
  if (!listDrawer) {
    return;
  }

  // Reset any form messages
  const messages = listDrawer.querySelectorAll<HTMLElement>('.form__message');
  messages?.forEach((message) => {
    message.setAttribute('aria-hidden', 'true');
  });

  listDrawer.classList.remove('open');
  listDrawer.setAttribute('aria-hidden', 'true');

  const pageBtns = getPageButtons();

  pageBtns.forEach((btn) => {
    btn.setAttribute('tabindex', '0');
    btn.removeAttribute('data-tabindex');
  });

  const tabbables = listDrawer.querySelectorAll<HTMLElement>('[data-tabindex="modified"]');
  tabbables.forEach((el) => {
    el.setAttribute('tabindex', '-1');
    el.removeAttribute('data-tabindex');
  });

  if (lastFocusedElement) {
    lastFocusedElement.focus({ preventScroll: true });
    lastFocusedElement = null;
  }

  enableScrolling();
}

function updateDrawerState(state: DrawerState) {
  drawerState = state;

  drawerPages.forEach((page) => {
    const isActive = page.dataset.state === drawerState;
    page.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });
}

listDrawer?.addEventListener('close', () => {
  if (createListForm) {
    resetForm(createListForm, null, false);
  }

  if (editListForm) {
    resetForm(editListForm, null, false);
  }
});

function updateSaveBtnState(form: HTMLFormElement) {
  const saveBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const checkboxes = form.querySelectorAll<HTMLInputElement>(
    'input[type="checkbox"][name="id"]'
  );

  const changesMade = Array.from(checkboxes).some((cb) => {
    const initial = cb.dataset.initial === 'true';
    return cb.checked !== initial;
  });

  if (saveBtn) {
    saveBtn.disabled = !changesMade;
  }
}

function getAddToListQuantity(triggerBtn: HTMLButtonElement): number {
  const triggerForm = triggerBtn.closest<HTMLFormElement>('form');

  // Order-item buttons include a hidden quantity input in the same form.
  const formQuantityInput = triggerForm?.querySelector<HTMLInputElement>('input[name="quantity"]');
  const formQuantity = formQuantityInput ? parseInt(formQuantityInput.value, 10) : NaN;

  if (Number.isInteger(formQuantity) && formQuantity > 0) {
    return formQuantity;
  }

  // PDP add-to-list button sits beside the quantity selector inside the buy form.
  const scopedQuantityInput = triggerForm?.querySelector<HTMLInputElement>('.js-quantity-selector .quantity__input');
  const scopedQuantity = scopedQuantityInput ? parseInt(scopedQuantityInput.value, 10) : NaN;

  if (Number.isInteger(scopedQuantity) && scopedQuantity > 0) {
    return scopedQuantity;
  }

  return 1;
}

async function addFormSubmit(e: Event) {
  const form = e.target as HTMLFormElement;
  e.preventDefault();

  const honey = form.querySelector<HTMLInputElement>('input[name="age"]');

  if (honey?.value) {
    return;
  };

  const productId = form.querySelector<HTMLInputElement>('input[name="productId"]')?.value;

  if (!productId) {
    console.error('No product ID found');
    return;
  }

  const listSelectionCheckboxes = form.querySelectorAll<HTMLInputElement>('input[type="checkbox"][name="id"]');
  const quantity = selectedAddToListQuantity;

  const listIdsToAddProductTo: string[] = [];
  const listIdsToRemoveProductFrom: string[] = [];
  let isInAnyList = false;

  listSelectionCheckboxes.forEach((checkbox) => {
    const wasInitiallyChecked = checkbox.dataset.initial === 'true';
    const isNowChecked = checkbox.checked;

    if (isNowChecked) {
      isInAnyList = true;
    }

    if (!wasInitiallyChecked && isNowChecked) {
      listIdsToAddProductTo.push(checkbox.value);
    } else if (wasInitiallyChecked && !isNowChecked) {
      listIdsToRemoveProductFrom.push(checkbox.value);
    }
  });

  // If there has been no change in checkboxes, don't do anything
  if (listIdsToAddProductTo.length === 0 && listIdsToRemoveProductFrom.length === 0) {
    return;
  }

  updateInputs(form, true); // disable inputs
  setLoading(form, true);

  try {
    await Promise.all(
      listIdsToAddProductTo.map(async (targetListId) => {
        const response = await fetch(`/api/users/lists/${targetListId}/items`, {
          method: 'POST',
          credentials: 'same-origin',
          body: JSON.stringify({
            products: [{
              productId: parseInt(productId, 10),
              quantity,
            }],
            _csrf: csrfToken,
          }),
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to add product to list ${targetListId}`);
        }
      })
    );

    await Promise.all(
      listIdsToRemoveProductFrom.map(async (targetListId) => {
        const formData = new FormData();
        formData.append('_csrf', csrfToken);
        // /api/users/lists/:id/items/:itemId
        const response = await fetch(`/api/users/lists/${targetListId}/items/${productId}`, {
          method: 'DELETE',
          credentials: 'same-origin',
          body: formData,
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to remove product from list ${targetListId}`);
        }
      })
    );

    await wait(450);

    const formMessage = listIdsToAddProductTo.length + listIdsToRemoveProductFrom.length === 1 ?
      'List successfully updated' : 'Lists successfully updated';

    showSuccess(form, formMessage);

    const checkboxes = form.querySelectorAll<HTMLInputElement>('.js-list-checkbox');

    checkboxes.forEach((checkbox) => {
      checkbox.dataset.initial = checkbox.checked ? 'true' : 'false';
    });

    // Set the aria-pressed attribute on the list button
    const pdpAtlBtn = document.querySelector<HTMLElement>('.pdp__atl');

    if (pdpAtlBtn) {
      pdpAtlBtn.setAttribute('aria-pressed', isInAnyList ? 'true' : 'false');

      // If we're on a PDP page, adjust the nav icon too
      document.querySelectorAll('.nav__icon-btn--list').forEach((btn) => {
        if (isInAnyList) {
          btn.classList.add('nav__icon-btn--solid');
        } else {
          btn.classList.remove('nav__icon-btn--solid');
        }
      });
    };

    await wait(1500);

    closeListDrawer();
  } catch (err) {
    console.error(err);
    showError(form);
    setLoading(form, false);
  } finally {
    updateInputs(form, false);
    setLoading(form, false);
    updateSaveBtnState(form);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  if (!addForm || !createListForm) {
    return;
  }

  updateDrawerState(drawerState);

  addToListBtns.forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!isLoggedIn) {
        updateDrawerState('loggedout');
        openListDrawer();
      } else {
        const productId = btn.dataset.productId;
        selectedAddToListQuantity = getAddToListQuantity(btn);

        if (productId) {
          await getListInfo(productId);
        }
      }
    });
  });

  createListBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const refreshAttr = btn.getAttribute('data-refresh');
      shouldRefreshAfterCreate = refreshAttr !== 'false';
      createListForm?.querySelector<HTMLElement>('.js-form-duplicate')?.setAttribute('aria-hidden', 'true');
      if (backBtn) {
        backBtn.style.display = shouldRefreshAfterCreate ? 'none' : '';
      }
      updateDrawerState('create');

      // Only open the drawer if it's not already open to avoid double scroll-lock
      if (!listDrawer.classList.contains('open')) {
        openListDrawer();
      }
    });
  });

  editListBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();

      const card = btn.closest<HTMLElement>('.js-list-card');

      if (!card || !editListForm) {
        return;
      }
      const { id, name, description } = card.dataset;

      setInput(editListForm, 'id', id);
      setInput(editListForm, 'name', name);
      setInput(editListForm, 'description', description);

      updateDrawerState('edit');
      openListDrawer();
    });
  });

  backBtn?.addEventListener('click', () => {
    updateDrawerState('add');
  });

  const pristineCreate = new Pristine(createListForm, {
    classTo: 'form__input-wrap',
    errorTextParent: 'form__input-wrap',
  });

  createListForm?.addEventListener('submit', async (e) => {
    e.preventDefault();

    const honey = createListForm.querySelector<HTMLInputElement>('input[name="age"]');
    const valid = pristineCreate.validate() && !honey?.value;

    if (valid) {
      const typedName = createListForm.querySelector<HTMLInputElement>('[name="name"]')?.value ?? '';
      const duplicateId = findDuplicateListId(typedName);

      if (duplicateId !== null) {
        const duplicateEl = createListForm.querySelector<HTMLElement>('.js-form-duplicate');
        const actionsEl = duplicateEl?.querySelector<HTMLElement>('.js-duplicate-actions');

        if (actionsEl) {
          const showActions = !shouldRefreshAfterCreate;
          actionsEl.setAttribute('aria-hidden', showActions ? 'false' : 'true');
          if (showActions) {
            const addBtn = actionsEl.querySelector<HTMLButtonElement>('.js-add-to-existing-list');
            if (addBtn) {
              addBtn.dataset.listId = duplicateId;
            }
          }
        }

        duplicateEl?.removeAttribute('aria-hidden');
        return;
      }

      const formData = new FormData(createListForm);
      formData.append('_csrf', csrfToken as string);
      updateInputs(createListForm, true); // disable inputs
      setLoading(createListForm, true);

      try {
        const response = await fetch('/api/users/lists', {
          method: 'POST',
          credentials: 'same-origin',
          body: formData,
          headers: {
            accept: 'application/json',
          },
        });

        // Add a little delay so we can see something is happening
        await wait(500);

        // @ts-ignore
        const { result } = await response.json();

        if (!response.ok) {
          showError(createListForm);
          updateInputs(createListForm, false); // enable inputs
          setLoading(createListForm, false);
          return;
        }

        showSuccess(createListForm);
        setLoading(createListForm, false);

        if (shouldRefreshAfterCreate) {
          location.reload();
        } else {
          const checkboxContainer = addForm?.querySelector('.js-list-checkboxes');

          if (checkboxContainer && result && result.id && result.name) {
            const newCheckbox = createListCheckbox(result.id, result.name);
            const newInput = newCheckbox.querySelector<HTMLInputElement>('input[type="checkbox"]')!;

            // grab existing checked checkboxes then uncheck any that weren't initially checked
            const existingCheckboxes =
              checkboxContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');

            existingCheckboxes.forEach((cb) => {
              if (cb.dataset.initial !== 'true') {
                cb.checked = false;
              }
            });

            newInput.checked = true;
            newInput.dataset.initial = 'false';

            // Append checkbox
            checkboxContainer.appendChild(newCheckbox);

            wait(500);
            updateDrawerState('add');
            resetForm(createListForm, null, false);

            // Focus the new checkbox for accessibility
            newInput.focus();

            checkboxContainer.querySelector('.list-drawer__message')?.remove();

            // Update save button state in case it's now enabled
            updateSaveBtnState(addForm!);

            checkboxContainer.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }
      } catch (error) {
        // Generic "error submitting form" message
        showError(createListForm);

        // enable inputs
        // updateInputs(form, false);
        setLoading(createListForm, false);
      }
    }
  });

  if (editListForm) {
    const pristineEdit = new Pristine(editListForm, {
      classTo: 'form__input-wrap',
      errorTextParent: 'form__input-wrap',
    });

    editListForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      const honey = editListForm.querySelector<HTMLInputElement>('input[name="age"]');
      const valid = pristineEdit.validate() && !honey?.value;

      if (valid) {
        const id = editListForm.querySelector<HTMLInputElement>('[name="id"]')?.value;
        const formData = new FormData(editListForm);
        formData.append('_csrf', csrfToken as string);
        updateInputs(editListForm, true); // disable inputs
        setLoading(editListForm, true);

        try {
          const response = await fetch(`/api/users/lists/${id}`, {
            method: 'PATCH',
            credentials: 'same-origin',
            body: formData,
            headers: {
              accept: 'application/json',
            },
          });

          if (!response.ok) {
            showError(editListForm);
            updateInputs(editListForm, false); // enable inputs
            setLoading(editListForm, false);
            return;
          }

          showSuccess(editListForm);
          setLoading(editListForm, false);
          await wait(500);
          location.reload();
        } catch (error) {
          // Generic "error submitting form" message
          showError(editListForm);

          // enable inputs
          // updateInputs(form, false);
          setLoading(editListForm, false);
        }
      }
    });
  }

  const checkboxContainer = addForm.querySelector('.js-list-checkboxes');
  checkboxContainer?.addEventListener('change', () => updateSaveBtnState(addForm));

  updateSaveBtnState(addForm);

  const messages = addForm.querySelectorAll<HTMLElement>('.form__message');

  messages?.forEach((message) => {
    let btn = message.querySelector('.js-form-message-dismiss');
    btn?.addEventListener('click', (e) => {
      e.preventDefault();
      message.setAttribute('aria-hidden', 'true');
    });
  });

  // Duplicate notification handlers (create form)
  const duplicateEl = createListForm?.querySelector<HTMLElement>('.js-form-duplicate');

  duplicateEl?.querySelector<HTMLButtonElement>('.js-form-message-dismiss')
    ?.addEventListener('click', () => {
      duplicateEl.setAttribute('aria-hidden', 'true');
    });

  duplicateEl?.querySelector<HTMLButtonElement>('.js-add-to-existing-list')
    ?.addEventListener('click', () => {
      const listId = duplicateEl.querySelector<HTMLButtonElement>('.js-add-to-existing-list')?.dataset.listId;
      if (!listId || !addForm) {
        return;
      }

      const checkbox = addForm.querySelector<HTMLInputElement>(`input[type="checkbox"][value="${listId}"]`);
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }

      resetForm(createListForm!, null, false);
      updateDrawerState('add');
      checkbox?.closest('.form__checkbox')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

  addForm.addEventListener('submit', (e) => {
    addFormSubmit(e);
  });
});

async function getListInfo(productId: string) {
  addForm?.classList.add('loading');
  updateDrawerState('add');
  openListDrawer();

  try {
    const response = await fetch(`/api/users/lists?productId=${productId}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken ?? '',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch list data');
    }

    const { result } = await response.json();
    const { lists, product } = result;

    updateProductInfo(product, listDrawer);

    addForm!.querySelector<HTMLInputElement>('[name="productId"]')!.value = product.id;

    const checkboxContainer = addForm?.querySelector<HTMLElement>('.js-list-checkboxes');

    checkboxContainer!.innerHTML = '';
    if (lists.length) {
      const isSingleList = lists.length === 1;

      lists.forEach((list) => {
        const checkbox = createListCheckbox(list.id, list.name);
        const input = checkbox.querySelector('input');

        const shouldBeChecked = isSingleList || list.containsProduct;

        input!.checked = shouldBeChecked;
        input!.dataset.initial = list.containsProduct ? 'true' : 'false';

        checkboxContainer?.appendChild(checkbox);
      });

      updateSaveBtnState(addForm!);
    } else {
      const emptyMsg = document.createElement('p');
      emptyMsg.classList.add('list-drawer__message');
      emptyMsg.textContent = 'Create a list to get started';
      checkboxContainer?.appendChild(emptyMsg);
      shouldRefreshAfterCreate = false;
      updateDrawerState('create');
    }
  } catch (err) {
    console.error('Error fetching lists', err);
    addForm?.classList.remove('loading');
  } finally {
    addForm?.classList.remove('loading');
  }
}

function findDuplicateListId(name: string): string | null {
  const normalized = name.trim().toLowerCase();

  // PDP context: lists are loaded as checkboxes
  const checkboxContainer = addForm?.querySelector('.js-list-checkboxes');
  if (checkboxContainer) {
    const labels = checkboxContainer.querySelectorAll<HTMLLabelElement>('.form__checkbox-label');
    for (const label of labels) {
      if (label.textContent?.trim().toLowerCase() === normalized) {
        const input = label.previousElementSibling as HTMLInputElement;
        return input?.value ?? null;
      }
    }
  }

  // Accounts page: lists are rendered as list cards with data-name
  const listCards = document.querySelectorAll<HTMLElement>('.js-list-card');
  for (const card of listCards) {
    if (card.dataset.name?.toLowerCase() === normalized) {
      return card.dataset.id ?? null;
    }
  }

  return null;
}

function createListCheckbox(id: string, name: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.classList.add('form__checkbox', 'form__checkbox--border');

  const checkboxId = `list-${id}`;

  wrapper.innerHTML = `
    <input
      class="form__checkbox-input js-list-checkbox"
      type="checkbox"
      id="${checkboxId}"
      name="id"
      value="${id}"
      data-initial="false"
    >
    <label class="form__checkbox-label" for="${checkboxId}">
      ${name}
    </label>
  `;

  return wrapper;
};

function updateProductInfo(product, listDrawer) {
  const title = listDrawer.querySelector('.list-drawer__product-title');
  if (title) {
    title.textContent = product.name;
  }

  const sku = listDrawer.querySelector('.list-drawer__product-meta-item');
  if (sku) {
    sku.textContent = `SKU #${product.sku}`;
  }

  const picture = listDrawer.querySelector('.list-drawer__product-image');
  if (picture) {
    const imageUrl = product.imageUrl ?? SMALL_PLACEHOLDER_IMAGE_URL;
    picture.innerHTML = `
    <source data-srcset="${generateTransformedImageUrl(imageUrl, { format: 'webp', fit: 'scale-down', width: 200 })}" type="image/webp" />
    <source data-srcset="${generateTransformedImageUrl(imageUrl, { format: 'jpeg', fit: 'scale-down', width: 200 })}" type="image/jpeg" />
    <img data-src="${generateTransformedImageUrl(imageUrl, { format: 'jpeg', fit: 'scale-down', width: 200 })}" alt="" class="lazyload" />
    `;
  }
}
